/**
 * Illustrations — full-scene artwork for info cards and empty states.
 *
 * Unlike `styles/customIcons.jsx` these are drawn at card size, so they have to
 * survive both themes *and* a coloured card behind them. Two roles do that:
 *
 *   ink      `currentColor` — inherits the card's text colour, so it is dark on
 *            a light card, light on a dark one, and white on a primary one
 *   surface  `var(--illu-surface)` — the colour the illustration sits on, used
 *            for the shapes that read as cut-outs rather than solid fills
 *
 * Everything in between is ink at a reduced opacity, never a fixed grey: a grey
 * that reads as "muted" on white reads as "invisible" on #111.
 *
 * A registry entry is a plain element, like `customIcons`. The svg is sized at
 * 100% of whatever box the caller gives it.
 */

// The line weight the isometric artwork is drawn with. Spread rather than
// repeated so a stroke cannot drift on one face of one box.
const ink = { stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }
const inkThin = { ...ink, strokeWidth: 1.5 }

// The three faces of a box: lit, shaded, shadowed.
const topFace = { fill: 'var(--illu-surface, transparent)' }
const leftFace = { fill: 'currentColor', fillOpacity: 0.2 }
const rightFace = { fill: 'currentColor', fillOpacity: 0.45 }

const paperPlane = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" width="100%" height="100%" fill="none">
        {/* Flight path arc */}
        <path
            d="M50,140 Q110,150 160,100 T230,55"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray="4,5"
            opacity={0.55}
        />

        {/* Originating document (left) */}
        <g transform="translate(40, 90) rotate(-10)">
            <rect
                x="0" y="0" width="50" height="65" rx="4"
                fill="var(--illu-surface, transparent)"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinejoin="round"
            />
            {/* Folded corner */}
            <path d="M36,0 L50,14 L36,14 Z" fill="currentColor" opacity={0.2} />
            <path
                d="M36,0 L36,14 L50,14"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Skeleton lines */}
            <rect x="8" y="18" width="24" height="4" rx="2" fill="currentColor" />
            <rect x="8" y="28" width="34" height="3" rx="1.5" fill="currentColor" opacity={0.5} />
            <rect x="8" y="36" width="30" height="3" rx="1.5" fill="currentColor" opacity={0.5} />
            <rect x="8" y="44" width="20" height="3" rx="1.5" fill="currentColor" opacity={0.5} />
        </g>

        {/* Flying paper airplane (right) */}
        <g transform="translate(210, 35) rotate(15)">
            <polygon
                points="0,25 45,0 20,35"
                fill="var(--illu-surface, transparent)"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinejoin="round"
            />
            <polygon points="45,0 20,35 28,18" fill="currentColor" opacity={0.85} />
            {/* Under-fold shadow */}
            <polygon points="20,35 28,18 10,27" fill="currentColor" opacity={0.5} />
        </g>

        {/* Sparkle accents */}
        <path
            d="M 260,30 L 262,35 L 267,37 L 262,39 L 260,44 L 258,39 L 253,37 L 258,35 Z"
            fill="currentColor"
        />
        <circle cx="120" cy="125" r="3" fill="currentColor" />
    </svg>
)

const products = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 220" width="100%" height="100%" fill="none">
        {/* Ground shadow */}
        <ellipse cx="160" cy="180" rx="95" ry="22" fill="currentColor" opacity={0.08} />

        {/* Motion trail */}
        <path
            d="M40,150 Q70,90 135,100"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray="4,5"
            opacity={0.55}
        />

        {/* Stacked box, behind right */}
        <g transform="translate(40, -10)">
            <polygon points="180,90 215,72 250,90 215,108" {...topFace} {...ink} />
            <polygon points="180,90 215,108 215,148 180,130" {...leftFace} {...ink} />
            <polygon points="215,108 250,90 250,130 215,148" {...rightFace} {...ink} />
            {/* Tape band across the top */}
            <polygon points="194,83 206,77 225,86 213,92" fill="currentColor" />
        </g>

        {/* Hero box, front centre */}
        <g>
            <polygon points="135,80 185,55 235,80 185,105" {...topFace} {...ink} />
            <polygon points="135,80 185,105 185,160 135,135" {...leftFace} {...ink} />
            <polygon points="185,105 235,80 235,135 185,160" {...rightFace} {...ink} />
            {/* Centre fold */}
            <line x1="185" y1="55" x2="185" y2="105" {...ink} opacity={0.3} />
            {/* Shipping label */}
            <polygon points="198,103 222,91 222,111 198,123" {...topFace} {...inkThin} />
            <line x1="203" y1="105" x2="214" y2="99.5" {...inkThin} />
            <line x1="203" y1="111" x2="217" y2="104" {...inkThin} />
            <polygon points="203,116 211,112 211,117 203,121" fill="currentColor" />
        </g>

        {/* Small box, foreground left */}
        <g transform="translate(-15, 25)">
            <polygon points="90,105 120,90 150,105 120,120" {...topFace} {...ink} />
            <polygon points="90,105 120,120 120,155 90,140" {...leftFace} {...ink} />
            <polygon points="120,120 150,105 150,140 120,155" {...rightFace} {...ink} />
            {/* This-side-up arrow */}
            <path d="M103,126 L107,122 L111,126 M107,122 L107,133" fill="none" {...inkThin} />
        </g>

        {/* Sparkle accents */}
        <path
            d="M 265,50 L 267,55 L 272,57 L 267,59 L 265,64 L 263,59 L 258,57 L 263,55 Z"
            fill="currentColor"
        />
        <circle cx="75" cy="75" r="3" fill="currentColor" />
        <circle cx="275" cy="115" r="2.5" fill="currentColor" />
    </svg>
)

const illustrations = {
    paperPlane,
    products,
}

export default illustrations
